import React, { useState } from "react";
import { Pencil, X, Check, Loader2 } from "lucide-react";
import api from "./api";

// ---------------------------------------------------------------------------
// Formatters (read-only mode)
// ---------------------------------------------------------------------------
const money = (n) =>
  n == null || n === ""
    ? "—"
    : new Intl.NumberFormat("en-CA", {
        style: "currency",
        currency: "CAD",
        maximumFractionDigits: 0,
      }).format(n);

const pct = (n) => (n == null || n === "" ? "—" : `${(Number(n) * 100).toFixed(2)}%`);
const yn = (v) => (v == null || v === "" ? "—" : String(v));
const date = (v) => (v ? String(v).slice(0, 10) : "—");

// Dot-path setter (returns a new object; supports "a.b.c" and numeric array segments like "props.0.value")
function setPath(obj, path, value) {
  const keys = path.split(".");
  const clone = Array.isArray(obj) ? [...obj] : { ...(obj || {}) };
  let cur = clone;
  for (let i = 0; i < keys.length - 1; i++) {
    const k = keys[i];
    const next = keys[i + 1];
    const nextIsIndex = /^\d+$/.test(next);
    const existing = cur[k];
    cur[k] = nextIsIndex
      ? Array.isArray(existing) ? [...existing] : []
      : existing && typeof existing === "object" && !Array.isArray(existing)
      ? { ...existing }
      : {};
    cur = cur[k];
  }
  cur[keys[keys.length - 1]] = value === "" ? null : value;
  return clone;
}

function getPath(obj, path) {
  return path.split(".").reduce((o, k) => (o == null ? undefined : o[k]), obj);
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
export default function FinancialProfile({ profile, contactId, onSaved }) {
  const [edit, setEdit] = useState(false);
  const [draft, setDraft] = useState(profile || null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // Sync draft when profile prop changes and we're not editing
  React.useEffect(() => {
    if (!edit) setDraft(profile || null);
  }, [profile, edit]);

  const startEdit = () => {
    // Seed empty scaffolds so users can add data on brand new profiles
    const seed = draft || {
      personal: {}, coverage: {}, medical: {}, investment: {}, employment: {},
      business: { corps: [] }, home: { properties: [] }, children: { children: [] },
      loans: {}, existing_coverage: [], existing_debt: [], investment_accounts: [],
      liquid_assets: [],
    };
    setDraft(seed);
    setEdit(true);
  };
  const cancelEdit = () => {
    setDraft(profile || null);
    setEdit(false);
    setError("");
  };
  const save = async () => {
    if (!contactId) return;
    // Ensure any focused input flushes its onBlur → draft before we read state
    if (typeof document !== "undefined" && document.activeElement && document.activeElement.blur) {
      document.activeElement.blur();
    }
    await new Promise((r) => setTimeout(r, 0));
    setSaving(true);
    setError("");
    try {
      await api.put(`/crm/profiles/${contactId}`, draft || {});
      setEdit(false);
      onSaved && onSaved();
    } catch (e) {
      setError(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const mutate = (path, value) => setDraft((d) => setPath(d || {}, path, value));

  // Empty-state (no profile yet)
  if (!profile && !edit) {
    return (
      <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-8 text-center">
        <div className="text-white/50 mb-4">
          No detailed financial profile on file for this contact.
        </div>
        {contactId && (
          <button
            onClick={startEdit}
            data-testid="profile-create-btn"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-black"
            style={{
              background:
                "linear-gradient(180deg, #f4d47b 0%, #d4a53a 55%, #b78a2f 100%)",
            }}
          >
            <Pencil size={14} /> Build profile
          </button>
        )}
      </div>
    );
  }

  const p = edit ? draft || {} : profile;
  const P = p.personal || {};
  const C = p.coverage || {};
  const I = p.investment || {};
  const E = p.employment || {};

  const ctx = { edit, mutate, _get: (path) => getPath(p, path) };

  return (
    <div className="space-y-5" data-testid="financial-profile">
      {/* Edit toolbar */}
      {contactId && (
        <div className="flex items-center justify-end gap-2">
          {error && (
            <div className="text-sm text-red-300/90 mr-auto" data-testid="profile-error">
              {error}
            </div>
          )}
          {edit ? (
            <>
              <button
                onClick={cancelEdit}
                disabled={saving}
                data-testid="profile-cancel"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-white/10 text-white/80 hover:bg-white/5 text-sm"
              >
                <X size={14} /> Cancel
              </button>
              <button
                onClick={save}
                disabled={saving}
                data-testid="profile-save"
                className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-sm font-medium text-black disabled:opacity-60"
                style={{
                  background:
                    "linear-gradient(180deg, #f4d47b 0%, #d4a53a 55%, #b78a2f 100%)",
                }}
              >
                {saving ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
                {saving ? "Saving…" : "Save profile"}
              </button>
            </>
          ) : (
            <button
              onClick={startEdit}
              data-testid="profile-edit"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-white/10 text-white/80 hover:bg-white/5 text-sm"
            >
              <Pencil size={14} /> Edit profile
            </button>
          )}
        </div>
      )}

      <Meta profile={p} ctx={ctx} />

      <TwoCol title="Personal — Contact & Partner" ctx={ctx}>
        <PairRow label="First Name" cp="personal.first_name.contact" pp="personal.first_name.partner" ctx={ctx} />
        <PairRow label="Last Name" cp="personal.last_name.contact" pp="personal.last_name.partner" ctx={ctx} />
        <PairRow label="Title (Mr/Mrs)" cp="personal.title_marital.contact" pp="personal.title_marital.partner" ctx={ctx} />
        <PairRow label="Marital Status" cp="personal.title_marital.contact_sub" pp="personal.title_marital.partner_sub" ctx={ctx} />
        <PairRow label="Email" cp="personal.email.contact" pp="personal.email.partner" ctx={ctx} />
        <PairRow label="Phone" cp="personal.phone.contact" pp="personal.phone.partner" ctx={ctx} />
        <PairRow label="Street Address" cp="personal.street_address.contact" pp="personal.street_address.partner" ctx={ctx} />
        <PairRow label="City" cp="personal.city_province_postal.contact" pp="personal.city_province_postal.partner" ctx={ctx} />
        <PairRow label="Province / Postal" cp="personal.city_province_postal.contact_sub" pp="personal.city_province_postal.partner_sub" ctx={ctx} />
        <PairRow label="Age" cp="personal.age_sex.contact" pp="personal.age_sex.partner" ctx={ctx} type="number" />
        <PairRow label="Sex" cp="personal.age_sex.contact_sub" pp="personal.age_sex.partner_sub" ctx={ctx} />
        <PairRow label="DOB" cp="personal.dob_smoking.contact" pp="personal.dob_smoking.partner" ctx={ctx} format={date} />
        <PairRow label="Smoking" cp="personal.dob_smoking.contact_sub" pp="personal.dob_smoking.partner_sub" ctx={ctx} />
        <PairRow label="Birth Place" cp="personal.birth_place.contact" pp="personal.birth_place.partner" ctx={ctx} />
        <PairRow label="Residency" cp="personal.residency.contact" pp="personal.residency.partner" ctx={ctx} />
        <PairRow label="Height / Weight" cp="personal.height_weight.contact" pp="personal.height_weight.partner" ctx={ctx} />
      </TwoCol>

      <Section title="Coverage">
        <Grid>
          <EField label="Main — Contact" path="coverage.amount_main_contact" ctx={ctx} v={C.amount_main_contact} format={money} type="number" />
          <EField label="2nd Option — Contact" path="coverage.amount_2nd_contact" ctx={ctx} v={C.amount_2nd_contact} format={money} type="number" />
          <EField label="Main — Partner" path="coverage.amount_main_partner" ctx={ctx} v={C.amount_main_partner} format={money} type="number" />
          <EField label="2nd Option — Partner" path="coverage.amount_2nd_partner" ctx={ctx} v={C.amount_2nd_partner} format={money} type="number" />
          <EField label="Period Main — Contact (yrs)" path="coverage.period_main_contact" ctx={ctx} v={C.period_main_contact ? `${C.period_main_contact} yrs` : "—"} type="number" />
          <EField label="Period Main — Partner (yrs)" path="coverage.period_main_partner" ctx={ctx} v={C.period_main_partner ? `${C.period_main_partner} yrs` : "—"} type="number" />
          <EField label="Reason Why" path="coverage.reason_why" ctx={ctx} v={C.reason_why} full multi />
        </Grid>
      </Section>

      <Section title="Medical">
        <EField label="Medical Notes" path="medical.medical_notes" ctx={ctx} v={p.medical?.medical_notes} full multi />
      </Section>

      <TwoCol title="Investment" ctx={ctx}>
        <PairRow label="Volatility Tolerance" cp="investment.volatility_tolerance.contact" pp="investment.volatility_tolerance.partner" ctx={ctx} />
        <PairRow label="SIN" cp="investment.sin.contact" pp="investment.sin.partner" ctx={ctx} />
        <PairRow label="RRSP Room" cp="investment.rrsp_room_contact" pp="investment.rrsp_room_partner" ctx={ctx} type="number" format={money} />
        <PairRow label="TFSA Room" cp="investment.tfsa_room_contact" pp="investment.tfsa_room_partner" ctx={ctx} type="number" format={money} />
        <PairRow label="Notes" cp="investment.notes" pp="" ctx={ctx} span />
      </TwoCol>

      <TwoCol title="Employment" ctx={ctx}>
        <PairRow label="Job Title" cp="employment.job_title.contact" pp="employment.job_title.partner" ctx={ctx} />
        <PairRow label="Employer" cp="employment.employer_name.contact" pp="employment.employer_name.partner" ctx={ctx} />
        <PairRow label="Employer Address" cp="employment.employer_address.contact" pp="employment.employer_address.partner" ctx={ctx} />
        <PairRow label="Annual Gross Income" cp="employment.annual_gross_income_contact" pp="employment.annual_gross_income_partner" ctx={ctx} type="number" format={money} />
        <PairRow label="Income Source" cp="employment.income_source_contact" pp="employment.income_source_partner" ctx={ctx} />
      </TwoCol>

      {/* Business / corps */}
      {(p.business?.corps?.length || edit) ? (
        <Section
          title={`Business Ownership (${p.business?.corps?.length || 0})`}
          onAdd={edit ? () => mutate("business.corps", [...(p.business?.corps || []), { slot: (p.business?.corps?.length || 0) + 1 }]) : null}
        >
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-widest text-white/50 border-b border-white/[0.06]">
                  <Thc>Slot</Thc><Thc>Name</Thc><Thc>Type</Thc><Thc>Corp #</Thc><Thc>CRA #</Thc><Thc>Started</Thc><Thc>Yrs</Thc><Thc className="text-right">Revenue</Thc>
                  {edit && <Thc />}
                </tr>
              </thead>
              <tbody>
                {(p.business?.corps || []).map((c, i) => (
                  <tr key={i} className="border-b border-white/[0.04]">
                    <ECell path={`business.corps.${i}.slot`} v={c.slot} ctx={ctx} />
                    <ECell path={`business.corps.${i}.name`} v={c.name} ctx={ctx} />
                    <ECell path={`business.corps.${i}.type`} v={c.type} ctx={ctx} />
                    <ECell path={`business.corps.${i}.corp_number`} v={c.corp_number} ctx={ctx} />
                    <ECell path={`business.corps.${i}.cra_number`} v={c.cra_number} ctx={ctx} />
                    <ECell path={`business.corps.${i}.start_date`} v={c.start_date} ctx={ctx} format={date} />
                    <ECell path={`business.corps.${i}.operating_years`} v={c.operating_years} ctx={ctx} type="number" />
                    <ECell path={`business.corps.${i}.annual_revenue`} v={c.annual_revenue} ctx={ctx} type="number" format={money} className="text-right tabular-nums" />
                    {edit && <RemoveCell onClick={() => mutate("business.corps", (p.business?.corps || []).filter((_, j) => j !== i))} />}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <EField label="Ownership Notes" path="business.ownership_notes" ctx={ctx} v={p.business?.ownership_notes} full multi note />
          <EField label="Shareholder & Trust Notes" path="business.shareholder_notes" ctx={ctx} v={p.business?.shareholder_notes} full multi note />
        </Section>
      ) : null}

      {/* Home & properties */}
      {(p.home || edit) ? (
        <Section
          title={`Home & Mortgage (${p.home?.properties?.filter?.((x) => x.market_value).length || 0} active)`}
          onAdd={edit ? () => mutate("home.properties", [...(p.home?.properties || []), { slot: (p.home?.properties?.length || 0) + 1 }]) : null}
        >
          <EField label="Home Type / Household Net Worth" path="home.home_type_networth" ctx={ctx} v={p.home?.home_type_networth} full />
          {(p.home?.properties?.length || edit) ? (
            <div className="overflow-x-auto mt-3">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[11px] uppercase tracking-widest text-white/50 border-b border-white/[0.06]">
                    <Thc>Slot</Thc><Thc>Owner</Thc><Thc className="text-right">Market Value</Thc><Thc className="text-right">Purchase / ACB</Thc><Thc className="text-right">Mortgage</Thc><Thc className="text-right">Rate</Thc><Thc className="text-right">Monthly</Thc><Thc>Purchased</Thc><Thc>Sale Yr</Thc>
                    {edit && <Thc />}
                  </tr>
                </thead>
                <tbody>
                  {(p.home?.properties || [])
                    .map((x, i) => (
                      <tr key={i} className="border-b border-white/[0.04]">
                        <ECell path={`home.properties.${i}.slot`} v={x.slot} ctx={ctx} />
                        <ECell path={`home.properties.${i}.owner`} v={x.owner} ctx={ctx} />
                        <ECell path={`home.properties.${i}.market_value`} v={x.market_value} ctx={ctx} type="number" format={money} className="text-right tabular-nums" />
                        <ECell path={`home.properties.${i}.purchase_price_acb`} v={x.purchase_price_acb} ctx={ctx} type="number" format={money} className="text-right tabular-nums" />
                        <ECell path={`home.properties.${i}.mortgage_balance`} v={x.mortgage_balance} ctx={ctx} type="number" format={money} className="text-right tabular-nums" />
                        <ECell path={`home.properties.${i}.mortgage_interest_rate`} v={x.mortgage_interest_rate} ctx={ctx} type="number" step="0.001" format={pct} className="text-right tabular-nums" />
                        <ECell path={`home.properties.${i}.mortgage_monthly_payment`} v={x.mortgage_monthly_payment} ctx={ctx} type="number" format={money} className="text-right tabular-nums" />
                        <ECell path={`home.properties.${i}.purchase_year`} v={x.purchase_year} ctx={ctx} type="number" />
                        <ECell path={`home.properties.${i}.planned_sale_year`} v={x.planned_sale_year} ctx={ctx} type="number" />
                        {edit && <RemoveCell onClick={() => mutate("home.properties", (p.home?.properties || []).filter((_, j) => j !== i))} />}
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          ) : null}
          <EField label="Extra Notes" path="home.extra_notes" ctx={ctx} v={p.home?.extra_notes} full multi note />
        </Section>
      ) : null}

      {/* Children */}
      {(p.children || edit) ? (
        <Section
          title={`Children (${p.children?.children?.length || 0})`}
          onAdd={edit ? () => mutate("children.children", [...(p.children?.children || []), { slot: (p.children?.children?.length || 0) + 1 }]) : null}
        >
          <Grid>
            <EField label="Cash-Value Solution Pref" path="children.cash_value_solution_pref" ctx={ctx} v={p.children?.cash_value_solution_pref} />
            <EField label="Budget" path="children.budget" ctx={ctx} v={p.children?.budget} />
            <EField label="RESP Option Shown" path="children.resp_shown_option" ctx={ctx} v={p.children?.resp_shown_option} />
          </Grid>
          <EField label="Family RESP Notes" path="children.family_resp_notes" ctx={ctx} v={p.children?.family_resp_notes} full multi note />
          <EField label="Family Planning Notes" path="children.family_planning_notes" ctx={ctx} v={p.children?.family_planning_notes} full multi note />
          {(p.children?.children?.length || edit) ? (
            <div className="overflow-x-auto mt-3">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[11px] uppercase tracking-widest text-white/50 border-b border-white/[0.06]">
                    <Thc>Slot</Thc><Thc>Name</Thc><Thc>Sex</Thc><Thc>DOB</Thc><Thc className="text-right">Age</Thc><Thc>SIN</Thc><Thc className="text-right">RESP Bal.</Thc><Thc className="text-right">RESP Grants</Thc><Thc className="text-right">Net Inv.</Thc>
                    {edit && <Thc />}
                  </tr>
                </thead>
                <tbody>
                  {(p.children?.children || []).map((ch, i) => (
                    <tr key={i} className="border-b border-white/[0.04]">
                      <ECell path={`children.children.${i}.slot`} v={ch.slot} ctx={ctx} />
                      <ECell path={`children.children.${i}.name`} v={ch.name} ctx={ctx} />
                      <ECell path={`children.children.${i}.sex`} v={ch.sex} ctx={ctx} />
                      <ECell path={`children.children.${i}.dob`} v={ch.dob} ctx={ctx} format={date} />
                      <ECell path={`children.children.${i}.age`} v={ch.age} ctx={ctx} type="number" className="text-right tabular-nums" />
                      <ECell path={`children.children.${i}.sin`} v={ch.sin} ctx={ctx} />
                      <ECell path={`children.children.${i}.resp_balance`} v={ch.resp_balance} ctx={ctx} type="number" format={money} className="text-right tabular-nums" />
                      <ECell path={`children.children.${i}.resp_grants_given`} v={ch.resp_grants_given} ctx={ctx} type="number" format={money} className="text-right tabular-nums" />
                      <ECell path={`children.children.${i}.resp_net_invested`} v={ch.resp_net_invested} ctx={ctx} type="number" format={money} className="text-right tabular-nums" />
                      {edit && <RemoveCell onClick={() => mutate("children.children", (p.children?.children || []).filter((_, j) => j !== i))} />}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
        </Section>
      ) : null}

      {/* Loans matrix */}
      {(p.loans || edit) ? (
        <Section title="Loans">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-widest text-white/50 border-b border-white/[0.06]">
                  <Thc> </Thc><Thc>Contact</Thc><Thc>Partner</Thc><Thc>Joint</Thc>
                </tr>
              </thead>
              <tbody>
                <LoanRow label="Student Loan Balance" base="loans.student_loan_balance" v={p.loans?.student_loan_balance} fmt={money} ctx={ctx} type="number" />
                <LoanRow label="Student Loan Rate" base="loans.student_loan_rate" v={p.loans?.student_loan_rate} fmt={pct} ctx={ctx} type="number" step="0.001" />
                <LoanRow label="Student Loan Monthly Pmt" base="loans.student_loan_monthly_pmt" v={p.loans?.student_loan_monthly_pmt} fmt={money} ctx={ctx} type="number" />
                <LoanRow label="Line of Credit Balance" base="loans.line_of_credit_balance" v={p.loans?.line_of_credit_balance} fmt={money} ctx={ctx} type="number" />
                <LoanRow label="Line of Credit Rate" base="loans.line_of_credit_rate" v={p.loans?.line_of_credit_rate} fmt={pct} ctx={ctx} type="number" step="0.001" />
                <LoanRow label="Line of Credit Monthly Pmt" base="loans.line_of_credit_monthly_pmt" v={p.loans?.line_of_credit_monthly_pmt} fmt={money} ctx={ctx} type="number" />
                <LoanRow label="Debt Consolidation Balance" base="loans.debt_consolidation_balance" v={p.loans?.debt_consolidation_balance} fmt={money} ctx={ctx} type="number" />
                <LoanRow label="Debt Consolidation Rate" base="loans.debt_consolidation_rate" v={p.loans?.debt_consolidation_rate} fmt={pct} ctx={ctx} type="number" step="0.001" />
                <LoanRow label="Debt Consolidation Monthly Pmt" base="loans.debt_consolidation_monthly_pmt" v={p.loans?.debt_consolidation_monthly_pmt} fmt={money} ctx={ctx} type="number" />
              </tbody>
            </table>
          </div>
        </Section>
      ) : null}

      {/* Existing coverage */}
      {((p.existing_coverage?.length) || edit) ? (
        <Section
          title={`Existing Coverage (${p.existing_coverage?.length || 0})`}
          onAdd={edit ? () => mutate("existing_coverage", [...(p.existing_coverage || []), {}]) : null}
        >
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-widest text-white/50 border-b border-white/[0.06]">
                  <Thc>Insured</Thc><Thc>Provider</Thc><Thc className="text-right">Coverage</Thc><Thc>Type</Thc><Thc>Term</Thc><Thc>Since</Thc><Thc className="text-right">Premium</Thc>
                  {edit && <Thc />}
                </tr>
              </thead>
              <tbody>
                {(p.existing_coverage || []).map((x, i) => (
                  <tr key={i} className="border-b border-white/[0.04]">
                    <ECell path={`existing_coverage.${i}.insured`} v={x.insured} ctx={ctx} />
                    <ECell path={`existing_coverage.${i}.provider`} v={x.provider} ctx={ctx} />
                    <ECell path={`existing_coverage.${i}.coverage`} v={x.coverage} ctx={ctx} type="number" format={money} className="text-right tabular-nums" />
                    <ECell path={`existing_coverage.${i}.type`} v={x.type} ctx={ctx} />
                    <ECell path={`existing_coverage.${i}.term_period`} v={x.term_period} ctx={ctx} />
                    <ECell path={`existing_coverage.${i}.year_started`} v={x.year_started} ctx={ctx} />
                    <ECell path={`existing_coverage.${i}.premium`} v={x.premium} ctx={ctx} type="number" format={money} className="text-right tabular-nums" />
                    {edit && <RemoveCell onClick={() => mutate("existing_coverage", (p.existing_coverage || []).filter((_, j) => j !== i))} />}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>
      ) : null}

      {/* Existing debt */}
      {((p.existing_debt?.length) || edit) ? (
        <Section
          title={`Existing Debt (${p.existing_debt?.length || 0})`}
          onAdd={edit ? () => mutate("existing_debt", [...(p.existing_debt || []), {}]) : null}
        >
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-widest text-white/50 border-b border-white/[0.06]">
                  <Thc>Loan Type</Thc><Thc>Provider</Thc><Thc className="text-right">Amount</Thc><Thc className="text-right">Monthly</Thc><Thc className="text-right">Yrs Left</Thc><Thc>Since</Thc>
                  {edit && <Thc />}
                </tr>
              </thead>
              <tbody>
                {(p.existing_debt || []).map((x, i) => (
                  <tr key={i} className="border-b border-white/[0.04]">
                    <ECell path={`existing_debt.${i}.loan_type`} v={x.loan_type} ctx={ctx} />
                    <ECell path={`existing_debt.${i}.provider`} v={x.provider} ctx={ctx} />
                    <ECell path={`existing_debt.${i}.amount_owed`} v={x.amount_owed} ctx={ctx} type="number" format={money} className="text-right tabular-nums" />
                    <ECell path={`existing_debt.${i}.monthly_payment`} v={x.monthly_payment} ctx={ctx} type="number" format={money} className="text-right tabular-nums" />
                    <ECell path={`existing_debt.${i}.years_left`} v={x.years_left} ctx={ctx} type="number" className="text-right tabular-nums" />
                    <ECell path={`existing_debt.${i}.year_started`} v={x.year_started} ctx={ctx} />
                    {edit && <RemoveCell onClick={() => mutate("existing_debt", (p.existing_debt || []).filter((_, j) => j !== i))} />}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>
      ) : null}

      {/* Investment accounts */}
      {((p.investment_accounts?.length) || edit) ? (
        <Section
          title={`Investment Accounts (${p.investment_accounts?.length || 0})`}
          onAdd={edit ? () => mutate("investment_accounts", [...(p.investment_accounts || []), {}]) : null}
        >
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-widest text-white/50 border-b border-white/[0.06]">
                  <Thc>Owner</Thc><Thc>Type</Thc><Thc>Account #</Thc><Thc>Source</Thc><Thc className="text-right">Balance</Thc><Thc className="text-right">ACB</Thc>
                  {edit && <Thc />}
                </tr>
              </thead>
              <tbody>
                {(p.investment_accounts || []).map((x, i) => (
                  <tr key={i} className="border-b border-white/[0.04]">
                    <ECell path={`investment_accounts.${i}.owner`} v={x.owner} ctx={ctx} />
                    <ECell path={`investment_accounts.${i}.type`} v={x.type} ctx={ctx} />
                    <ECell path={`investment_accounts.${i}.account_number`} v={x.account_number} ctx={ctx} />
                    <ECell path={`investment_accounts.${i}.source`} v={x.source} ctx={ctx} />
                    <ECell path={`investment_accounts.${i}.balance`} v={x.balance} ctx={ctx} type="number" format={money} className="text-right tabular-nums" />
                    <ECell path={`investment_accounts.${i}.acb`} v={x.acb} ctx={ctx} type="number" format={money} className="text-right tabular-nums" />
                    {edit && <RemoveCell onClick={() => mutate("investment_accounts", (p.investment_accounts || []).filter((_, j) => j !== i))} />}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>
      ) : null}

      {/* Liquid Assets */}
      {((p.liquid_assets?.length) || edit) ? (
        <Section
          title={`Existing Liquid Assets (${p.liquid_assets?.length || 0})`}
          onAdd={edit ? () => mutate("liquid_assets", [...(p.liquid_assets || []), {}]) : null}
        >
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-widest text-white/50 border-b border-white/[0.06]">
                  <Thc>Owner</Thc><Thc>Asset Type</Thc><Thc>Institution</Thc><Thc className="text-right">Invested</Thc><Thc className="text-right">Monthly PAC</Thc><Thc>Purpose</Thc>
                  {edit && <Thc />}
                </tr>
              </thead>
              <tbody>
                {(p.liquid_assets || []).map((x, i) => (
                  <tr key={i} className="border-b border-white/[0.04]">
                    <ECell path={`liquid_assets.${i}.owner`} v={x.owner} ctx={ctx} />
                    <ECell path={`liquid_assets.${i}.asset_type`} v={x.asset_type} ctx={ctx} />
                    <ECell path={`liquid_assets.${i}.institution`} v={x.institution} ctx={ctx} />
                    <ECell path={`liquid_assets.${i}.amount_invested`} v={x.amount_invested} ctx={ctx} type="number" format={money} className="text-right tabular-nums" />
                    <ECell path={`liquid_assets.${i}.monthly_pac`} v={x.monthly_pac} ctx={ctx} type="number" format={money} className="text-right tabular-nums" />
                    <ECell path={`liquid_assets.${i}.purpose`} v={x.purpose} ctx={ctx} />
                    {edit && <RemoveCell onClick={() => mutate("liquid_assets", (p.liquid_assets || []).filter((_, j) => j !== i))} />}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function Meta({ profile, ctx }) {
  const rows = [
    ["Annual Premium", "estimated_deal_size", profile.estimated_deal_size, money, "number"],
    ["Will Status", "will_status", profile.will_status, null],
    ["Form Template", "form_template", profile.form_template, null],
    ["Plan Review Month", "plan_review_month", profile.plan_review_month, null],
    ["Last Annual Review", "last_annual_review", profile.last_annual_review, date],
    ["Date Arrived", "date_arrived_in_system", profile.date_arrived_in_system, date],
    ["Data Collection Completed", "data_collection_completion_date", profile.data_collection_completion_date, date],
    ["Detailed Planning Attended", "detailed_planning_attendance_date", profile.detailed_planning_attendance_date, date],
    ["Committed Date", "contact_commitment_date", profile.contact_commitment_date, date],
    ["Became Client", "contact_became_client_date", profile.contact_became_client_date, date],
  ];
  return (
    <Section title="Case Meta">
      <Grid>
        {rows.map(([label, path, v, format, type]) => (
          <EField key={path} label={label} path={path} v={v} format={format} type={type} ctx={ctx} />
        ))}
      </Grid>
    </Section>
  );
}

function Section({ title, children, onAdd }) {
  return (
    <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-5">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm text-white/70 uppercase tracking-widest">{title}</h3>
        {onAdd && (
          <button
            onClick={onAdd}
            data-testid={`add-row-${title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            className="text-xs text-amber-200 hover:text-amber-100 border border-amber-300/30 rounded-full px-3 py-1"
          >
            + Add row
          </button>
        )}
      </div>
      {children}
    </div>
  );
}

function Grid({ children }) {
  return <div className="grid grid-cols-1 md:grid-cols-2 gap-y-3 gap-x-6 text-sm">{children}</div>;
}

function EField({ label, path, v, ctx, full, multi, format, type = "text", step, note }) {
  const display = format ? format(v) : (v == null || v === "" ? "—" : String(v));
  if (ctx?.edit) {
    return (
      <div className={full ? "md:col-span-2" : ""}>
        <div className="text-white/50 text-xs uppercase tracking-wider mb-1">{label}</div>
        {multi ? (
          <textarea
            rows={note ? 3 : 2}
            defaultValue={v ?? ""}
            onBlur={(e) => ctx.mutate(path, e.target.value)}
            data-testid={`edit-${path}`}
            className="w-full px-3 py-2 rounded-lg bg-black/30 border border-white/10 text-sm text-white/90 placeholder-white/30 focus:outline-none focus:border-amber-300/40 resize-y"
          />
        ) : (
          <input
            type={type}
            step={step}
            defaultValue={v ?? ""}
            onBlur={(e) => ctx.mutate(path, type === "number" ? (e.target.value === "" ? null : Number(e.target.value)) : e.target.value)}
            data-testid={`edit-${path}`}
            className="w-full px-3 py-2 rounded-lg bg-black/30 border border-white/10 text-sm text-white/90 placeholder-white/30 focus:outline-none focus:border-amber-300/40"
          />
        )}
      </div>
    );
  }
  return (
    <div className={full ? "md:col-span-2" : ""}>
      <div className="text-white/50 text-xs uppercase tracking-wider">{label}</div>
      <div className={"text-white/90 mt-0.5 break-words " + (note ? "whitespace-pre-wrap" : "")}>{display}</div>
    </div>
  );
}

function ECell({ path, v, ctx, type = "text", step, format, className = "" }) {
  const display = format ? format(v) : yn(v);
  if (ctx?.edit) {
    return (
      <td className={`py-1 pr-2 align-top ${className}`}>
        <input
          type={type}
          step={step}
          defaultValue={v ?? ""}
          onBlur={(e) => ctx.mutate(path, type === "number" ? (e.target.value === "" ? null : Number(e.target.value)) : e.target.value)}
          data-testid={`edit-${path}`}
          className="w-full px-2 py-1 rounded-md bg-black/30 border border-white/10 text-xs text-white/90 focus:outline-none focus:border-amber-300/40"
        />
      </td>
    );
  }
  return <td className={`py-2 pr-4 align-top ${className}`}>{display}</td>;
}

function RemoveCell({ onClick }) {
  return (
    <td className="py-1 pr-2 align-top">
      <button
        onClick={onClick}
        className="text-white/40 hover:text-red-300 text-xs"
        aria-label="Remove row"
      >
        ✕
      </button>
    </td>
  );
}

function Thc({ children, className = "" }) {
  return <th className={`py-2 pr-4 font-normal ${className}`}>{children || " "}</th>;
}

function LoanRow({ label, base, v, fmt, ctx, type, step }) {
  const c = v?.contact, pp = v?.partner, j = v?.joint;
  if (!ctx.edit && c == null && pp == null && j == null) return null;
  return (
    <tr className="border-b border-white/[0.04]">
      <td className="py-2 pr-4 align-top text-white/70">{label}</td>
      <ECell path={`${base}.contact`} v={c} ctx={ctx} type={type} step={step} format={fmt} className="tabular-nums" />
      <ECell path={`${base}.partner`} v={pp} ctx={ctx} type={type} step={step} format={fmt} className="tabular-nums" />
      <ECell path={`${base}.joint`} v={j} ctx={ctx} type={type} step={step} format={fmt} className="tabular-nums" />
    </tr>
  );
}

function TwoCol({ title, children, ctx }) {
  return (
    <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-5">
      {title && (
        <h3 className="text-sm text-white/70 uppercase tracking-widest mb-4">{title}</h3>
      )}
      <div className="grid grid-cols-[minmax(180px,240px)_1fr_1fr] gap-x-6 gap-y-2 text-sm items-baseline">
        <div className="text-[11px] uppercase tracking-widest text-white/40"> </div>
        <div className="text-[11px] uppercase tracking-widest text-amber-200/80">Contact</div>
        <div className="text-[11px] uppercase tracking-widest text-blue-200/80">Partner</div>
        {children}
      </div>
    </div>
  );
}

function PairRow({ label, cp, pp, ctx, span, type = "text", format }) {
  return (
    <>
      <div className="text-white/50">{label}</div>
      <PairCellInner path={cp} ctx={ctx} type={type} format={format} span={span} />
      {!span ? <PairCellInner path={pp} ctx={ctx} type={type} format={format} /> : null}
    </>
  );
}

function PairCellInner({ path, ctx, type, format, span }) {
  const v = ctx._get ? ctx._get(path) : null;
  if (!path) return <div />;
  if (ctx.edit) {
    return (
      <input
        type={type}
        defaultValue={v ?? ""}
        onBlur={(e) => ctx.mutate(path, type === "number" ? (e.target.value === "" ? null : Number(e.target.value)) : e.target.value)}
        data-testid={`edit-${path}`}
        className={`px-2 py-1.5 rounded-md bg-black/30 border border-white/10 text-sm text-white/90 focus:outline-none focus:border-amber-300/40 w-full ${span ? "col-span-2" : ""}`}
      />
    );
  }
  return <div className={`text-white/90 break-words ${span ? "col-span-2" : ""}`}>{format ? format(v) : yn(v)}</div>;
}
